import React, { useState, useRef, useEffect } from "react";

const SMSChatWindow = ({
    activePhone,
    messages,
    sending,
    onSendMessage,
    onShowUserInfo,
    onBackToList,
    userInfo,
    t
}) => {
    const [text, setText] = useState("");
    const messagesEndRef = useRef(null);

    // Auto-scroll to bottom of chat on message list changes
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (text.trim() && !sending) {
            onSendMessage(text.trim());
            setText("");
        }
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return "";
        const date = new Date(timestamp * 1000);
        return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    };

    const formatDateHeader = (timestamp) => {
        if (!timestamp) return "";
        const date = new Date(timestamp * 1000);
        return date.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    };

    // Group messages by day to display date headers
    const renderedMessages = [];
    let lastDateStr = null;

    messages.forEach((msg, idx) => {
        const msgDate = new Date(msg.date * 1000);
        const dateStr = msgDate.toDateString();

        if (dateStr !== lastDateStr) {
            renderedMessages.push(
                <div key={`date-${msg.message_id || idx}`} className="text-center my-3" style={{ zIndex: 1 }}>
                    <span 
                        style={{ 
                            backgroundColor: "rgba(225, 230, 235, 0.9)", 
                            padding: "4px 10px", 
                            borderRadius: "6px", 
                            fontSize: "11px", 
                            fontWeight: "600",
                            color: "#54656f",
                            boxShadow: "0 1px 1px rgba(0,0,0,0.05)"
                        }}
                    >
                        {formatDateHeader(msg.date)}
                    </span>
                </div>
            );
            lastDateStr = dateStr;
        }

        const isOutgoing = msg.direction === 'out';
        const priceValue = msg.message_price !== undefined ? msg.message_price : (msg.price !== undefined ? msg.price : (msg.cost !== undefined ? msg.cost : 0));

        renderedMessages.push(
            <div
                key={msg.message_id || idx}
                className={`sms-message-wrapper ${isOutgoing ? "outgoing" : "incoming"}`}
            >
                <div className="sms-message-bubble">
                    <div className="sms-message-body">{msg.body}</div>
                    
                    <span className="sms-message-time" dir="ltr">
                        {formatTime(msg.date)}
                        {isOutgoing && (
                            <span className="sms-message-status ml-1" style={{ color: msg.status === 'Failed' ? '#ef4444' : '#667781' }}>
                                {msg.status === 'Failed' ? (
                                    <i className="fas fa-exclamation-circle text-danger" title="Failed"></i>
                                ) : (
                                    <i className="fas fa-check" title={msg.status}></i>
                                )}
                            </span>
                        )}
                    </span>

                    {/* Cost/Price indicator */}
                    {isOutgoing && (
                        <div className="sms-price-indicator mt-1" style={{ fontSize: '9px', color: msg.status === 'Failed' ? '#ef4444' : '#667781', textAlign: 'left', fontStyle: 'italic' }}>
                            {t("Pages.SMS.price") || "السعر"}: ${priceValue}
                            {msg.status === 'Failed' && (
                                <span className="mr-1 text-danger">
                                    ({t("Pages.SMS.deliveryFailed") || "فشل الإرسال"})
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    });

    const isSystemUser = userInfo && !userInfo.isWhatsAppOnly;

    return (
        <div className="sms-main-chat">
            {/* Header */}
            <div className="sms-chat-header align-items-center">
                <div className="sms-chat-header-info d-flex align-items-center">
                    <button
                        type="button"
                        className="btn btn-link p-0 ml-2 sms-back-btn text-dark"
                        onClick={onBackToList}
                        title={t("Close") || "رجوع"}
                        style={{ fontSize: '20px', border: 'none', background: 'transparent', cursor: 'pointer' }}
                    >
                        <i className="fal fa-arrow-right"></i>
                    </button>
                    {isSystemUser && userInfo.photo ? (
                        <div className="sms-chat-header-avatar ml-2" style={{ width: '40px', height: '40px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
                            <img 
                                src={userInfo.photo.startsWith("http") ? userInfo.photo : (process.env.REACT_APP_BASE_URL || "") + (userInfo.photo.startsWith("/") ? "" : "/") + userInfo.photo} 
                                alt={userInfo.firstName} 
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                            />
                        </div>
                    ) : isSystemUser ? (
                        <div className="sms-chat-header-avatar ml-2 d-flex align-items-center justify-content-center bg-success text-white font-weight-bold" style={{ width: '40px', height: '40px', borderRadius: '50%', fontSize: '16px', flexShrink: 0 }}>
                            {String(userInfo.firstName || "?").substring(0, 1).toUpperCase()}
                        </div>
                    ) : (
                        <i className="fal fa-comment-alt-lines ml-2 text-success" style={{ fontSize: '20px' }}></i>
                    )}
                    
                    <div className="d-flex flex-column">
                        <h5 className="sms-active-name mb-0 font-weight-bold" dir="ltr" style={{ fontSize: '15px' }}>
                            {isSystemUser ? `${userInfo.firstName} ${userInfo.lastName}` : activePhone}
                        </h5>
                        {isSystemUser && (
                            <small className="text-muted" dir="ltr" style={{ fontSize: '11px' }}>{activePhone}</small>
                        )}
                    </div>
                </div>
                <div>
                    <button className="sms-icon-btn" onClick={() => onShowUserInfo(activePhone)} title={t("Pages.SMS.userInfo") || "بيانات المستخدم"}>
                        <i className="fad fa-user-shield"></i>
                        <span className="mr-1">{t("Pages.SMS.details") || "التفاصيل"}</span>
                    </button>
                </div>
            </div>

            {/* Message Stream */}
            <div className="sms-messages-area">
                {renderedMessages}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Bar */}
            <form onSubmit={handleSubmit} className="sms-input-bar">
                <input
                    type="text"
                    className="sms-input-field"
                    placeholder={t("Pages.SMS.typeMessage") || "اكتب رسالة نصية..."}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    disabled={sending}
                    required
                />
                <button
                    type="submit"
                    className="sms-send-btn"
                    disabled={sending || !text.trim()}
                    title={t("Pages.SMS.send") || "إرسال"}
                >
                    {sending ? (
                        <i className="fa-spin fas fa-spinner"></i>
                    ) : (
                        <i className="fas fa-paper-plane"></i>
                    )}
                </button>
            </form>
        </div>
    );
};

export default SMSChatWindow;
